import { gql, useMutation, useQuery } from '@apollo/client';
import TextArea from '@orn/shared-web/components/Textarea';
import * as React from 'react';

import Button from '../../components/Button';
import { useToast } from '../../components/Toast';
import { useAuthState } from '../../providers/AuthProvider';

import AssistantFeedbackComment from './AssistantFeedbackComment';

import type {
  AdminListAssistantFeedbackCommentsQuery,
  AdminListAssistantFeedbackCommentsQueryVariables,
} from './__generated__/AssistantFeedbackThread.gql';
import type {
  AdminAddAssistantFeedbackCommentMutation,
  AdminAddAssistantFeedbackCommentMutationVariables,
  AdminDeleteAssistantFeedbackCommentMutation,
  AdminDeleteAssistantFeedbackCommentMutationVariables,
} from './__generated__/AssistantFeedbackThread.gql';

const FEEDBACK_COMMENT_FIELDS = gql`
  fragment FeedbackCommentFields on AssistantFeedbackComment {
    id
    feedbackId
    authorUserId
    authorEmail
    transition
    body
    createdAt
  }
`;

const ADMIN_LIST_FEEDBACK_COMMENTS = gql`
  query AdminListAssistantFeedbackComments($feedbackId: ID!) {
    adminListAssistantFeedbackComments(feedbackId: $feedbackId) {
      ...FeedbackCommentFields
    }
  }
  ${FEEDBACK_COMMENT_FIELDS}
`;

const ADMIN_ADD_FEEDBACK_COMMENT = gql`
  mutation AdminAddAssistantFeedbackComment($feedbackId: ID!, $body: String!) {
    adminAddAssistantFeedbackComment(feedbackId: $feedbackId, body: $body) {
      ...FeedbackCommentFields
    }
  }
  ${FEEDBACK_COMMENT_FIELDS}
`;

const ADMIN_DELETE_FEEDBACK_COMMENT = gql`
  mutation AdminDeleteAssistantFeedbackComment($id: ID!) {
    adminDeleteAssistantFeedbackComment(id: $id) {
      id
      feedbackId
    }
  }
`;

interface IAssistantFeedbackThreadProps {
  feedbackId: string;
  /** Fired once the comment list has resolved — lets the parent settle the deep-link scroll after the row's height changes. */
  onCommentsLoaded?: (feedbackId: string) => void;
}

/**
 * Inline triage thread for one feedback row: lazily loads the row's comments
 * (mounted only while the row is expanded) and offers a free-form "Add comment"
 * box. Adding a comment refetches the thread and bumps the parent row's
 * `commentCount` in the Apollo cache so the table column stays in sync without a
 * full list refetch.
 */
const AssistantFeedbackThread = ({ feedbackId, onCommentsLoaded }: IAssistantFeedbackThreadProps) => {
  const { showToast } = useToast();
  const { user: mopsUser } = useAuthState();
  const [draft, setDraft] = React.useState('');
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const notifiedRef = React.useRef(false);

  const { data, loading, refetch } = useQuery<
    AdminListAssistantFeedbackCommentsQuery,
    AdminListAssistantFeedbackCommentsQueryVariables
  >(ADMIN_LIST_FEEDBACK_COMMENTS, {
    variables: { feedbackId },
    fetchPolicy: 'cache-and-network',
  });

  const comments = data?.adminListAssistantFeedbackComments ?? [];

  React.useEffect(() => {
    if (!notifiedRef.current && data) {
      notifiedRef.current = true;
      onCommentsLoaded?.(feedbackId);
    }
  }, [data, feedbackId, onCommentsLoaded]);

  const [addComment, { loading: adding }] = useMutation<
    AdminAddAssistantFeedbackCommentMutation,
    AdminAddAssistantFeedbackCommentMutationVariables
  >(ADMIN_ADD_FEEDBACK_COMMENT, {
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: 'AssistantFeedback', id: feedbackId }),
        fields: {
          commentCount: (existing: number | null) => (existing ?? 0) + 1,
        },
      });
    },
  });

  const handleAdd = async () => {
    const body = draft.trim();
    if (body.length === 0) {
      return;
    }
    await addComment({ variables: { feedbackId, body } });
    setDraft('');
    await refetch();
    showToast({ type: 'success', message: 'Comment added' });
  };

  const [deleteComment] = useMutation<
    AdminDeleteAssistantFeedbackCommentMutation,
    AdminDeleteAssistantFeedbackCommentMutationVariables
  >(ADMIN_DELETE_FEEDBACK_COMMENT, {
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: 'AssistantFeedback', id: feedbackId }),
        fields: {
          commentCount: (existing: number | null) => Math.max(0, (existing ?? 0) - 1),
        },
      });
    },
  });

  const handleDelete = async (commentId: string) => {
    setDeletingId(commentId);
    try {
      await deleteComment({ variables: { id: commentId } });
      await refetch();
      showToast({ type: 'success', message: 'Comment deleted' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 px-3 py-3">
      {loading && comments.length === 0 ? (
        <div className="text-fg-secondary text-xs">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No comments yet.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {comments.map((comment) => (
            <AssistantFeedbackComment
              key={comment.id}
              authorEmail={comment.authorEmail ?? null}
              authorUserId={comment.authorUserId}
              transition={comment.transition ?? null}
              body={comment.body}
              createdAt={comment.createdAt}
              canDelete={comment.authorUserId === mopsUser?.id && comment.transition == null}
              isDeleting={deletingId === comment.id}
              onDelete={() => void handleDelete(comment.id)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <TextArea
          label="Add comment"
          placeholder="Operator note — markdown supported"
          value={draft}
          onChange={setDraft}
          rows={2}
        />
        <div className="flex justify-end">
          <Button
            color="secondary"
            size="sm"
            isDisabled={draft.trim().length === 0 || adding}
            isLoading={adding}
            onClick={() => void handleAdd()}
          >
            Add comment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssistantFeedbackThread;
